import { Dispatch, SetStateAction, useState } from "react";
import { useForm } from "react-hook-form";
import { Contacto, CreateContactoRequest } from "../../../../../types/empresa";
import { InputDatosGenerales } from "../../../../../components/ui/InputDatosGenerales";
import { useCreateContactoMutation } from "src/features/empresas/empresaApi";
import { getErrorMessage } from "../../../../../utils/errorHelpers";
import { ErrorMessage } from "../../../../../components/errors/ErrorMessage";
import { ActionButtons } from "../../../../../components/ui/ActionButtons";
import Person2RoundedIcon from '@mui/icons-material/Person2Rounded';
import WorkRoundedIcon from '@mui/icons-material/WorkRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import LocalPhoneRoundedIcon from '@mui/icons-material/LocalPhoneRounded';
import PhoneAndroidRoundedIcon from '@mui/icons-material/PhoneAndroidRounded';
import ArticleRoundedIcon from '@mui/icons-material/ArticleRounded';

interface Props {
    empresaId: number;
    setShowCreate: Dispatch<SetStateAction<boolean>>;
    showCreate: boolean;
}

export function CreateContactoInfo({ setShowCreate, showCreate, empresaId }: Props) {
    const [createContacto, { isLoading }] = useCreateContactoMutation();
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const iconSize = 20;

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<Contacto>();

    const submit = handleSubmit(async (data: Contacto) => {
        try {
            setErrorMessage(null);

            const request: CreateContactoRequest = {
                empresaId: empresaId,
                contactoInfo: data
            }

            await createContacto(request).unwrap();

            setShowCreate(false);
            
        } catch (error) {
            setErrorMessage(getErrorMessage(error));
        }
    }) 

    return (
        <div>
            <p className="text-lg font-semibold mb-[16px]">Nuevo contacto</p>
             
            <form onSubmit={submit} autoComplete="off">
                <InputDatosGenerales
                    icon={<Person2RoundedIcon sx={{ height: iconSize, width: iconSize }}/>}
                    label={"Nombre"}
                    register={register("nombre", {
                        required: "El nombre es requerido",
                        maxLength: 50,
                    })}/>
                {errors.nombre?.type === "required" && (
                    <p className="valErrorText text-sm">{errors.nombre.message}</p>
                )}
                {errors.nombre?.type === "maxLength" && (
                    <p className="valErrorText text-sm">{"Maximo de caracteres: 50"}</p>
                )}

                <InputDatosGenerales
                    icon={<WorkRoundedIcon sx={{ height: iconSize, width: iconSize }}/>}
                    label={"Puesto"}
                    register={register("puesto", {
                        required: "El puesto es requerido",
                        maxLength: 30,
                    })}/>
                {errors.puesto?.type === "required" && (
                    <p className="valErrorText text-sm">{errors.puesto.message}</p>
                )}
                {errors.puesto?.type === "maxLength" && (
                    <p className="valErrorText text-sm">{"Maximo de caracteres: 30"}</p>
                )}

                <InputDatosGenerales
                    icon={<EmailRoundedIcon sx={{ height: iconSize, width: iconSize }}/>}
                    label={"Correo"}
                    register={register("correo", {
                        required: "El correo es requerido",
                        maxLength: 30,
                    })}/>
                {errors.correo?.type === "required" && (
                    <p className="valErrorText text-sm">{errors.correo.message}</p>
                )}
                {errors.correo?.type === "maxLength" && (
                    <p className="valErrorText text-sm">{"Maximo de caracteres: 30"}</p>
                )}

                <InputDatosGenerales
                    icon={<LocalPhoneRoundedIcon sx={{ height: iconSize, width: iconSize }}/>}
                    label={"Telefono empresa"}
                    register={register("telefonoEmpresa", {
                        maxLength: 10,
                    })}/>
                {errors.telefonoEmpresa?.type === "maxLength" && (
                    <p className="valErrorText text-sm">{"Maximo de caracteres: 10"}</p>
                )}

                <InputDatosGenerales
                    icon={<PhoneAndroidRoundedIcon sx={{ height: iconSize, width: iconSize }}/>}
                    label={"Telefono personal"}
                    register={register("telefonoPersonal", {
                        maxLength: 10,
                    })}/>
                {errors.telefonoPersonal?.type === "maxLength" && (
                    <p className="valErrorText text-sm">{"Maximo de caracteres: 10"}</p>
                )}

                <div className="w-full mb-[11px]">
                    <div className="flex items-center gap-x-2">
                        <ArticleRoundedIcon sx={{ height: iconSize, width: iconSize }}/>
                        <label className="font-semibold min-w-[140px]">Comentarios</label>
                    </div>
                    <textarea
                        {...register("comentarios")}
                        className="
                            w-full h-[100px] rounded-[3px] border-gray-400 border my-1 px-2 
                            font-semibold text-gray-600 text-sm resize-none">
                    </textarea>
                </div>

                {errorMessage && (
                    <div className="my-2">
                        <ErrorMessage
                            customMessage>
                            <p className="text-sm">{errorMessage}</p>
                        </ErrorMessage>
                    </div>
                )}

                <ActionButtons
                    isLoading={isLoading}
                    saveButtonText="Crear contacto"
                    onCancel={() => setShowCreate(false)}/>
            </form>
        </div>
    );
}
